/**
 * Provider Opportunity Service — Wave 1.5 ops selection (E14 / D-015 / D-016)
 */

import api from "@/shared/api";
import { logError } from "@/shared/utils/logger.js";

/**
 * Resolve ProviderOpportunity for an order (D-016).
 * @param {string} orderId
 * @returns {Promise<object|null>} opportunity or null when 404
 */
export async function getOpportunityByOrderId(orderId) {
  if (!orderId) return null;
  try {
    const response = await api.get(
      `/provider-opportunities/by-order/${encodeURIComponent(orderId)}`
    );
    if (response?.success === false) {
      return null;
    }
    return response?.opportunity ?? response?.data?.opportunity ?? null;
  } catch (error) {
    if (error?.status === 404) {
      return null;
    }
    logError("providerOpportunityService.getOpportunityByOrderId failed", {
      orderId,
      error: error?.message,
    });
    throw error;
  }
}

/**
 * List interested candidates for a ProviderOpportunity (D-012).
 * @param {string} opportunityId
 * @returns {Promise<object[]>}
 */
export async function getCandidates(opportunityId) {
  if (!opportunityId) return [];
  try {
    const response = await api.get(
      `/provider-opportunities/${encodeURIComponent(opportunityId)}/candidates`
    );
    if (response?.success === false) {
      throw new Error(response.error || "Failed to load candidates");
    }
    const candidates =
      response?.candidates ?? response?.data?.candidates ?? [];
    return Array.isArray(candidates) ? candidates : [];
  } catch (error) {
    logError("providerOpportunityService.getCandidates failed", {
      opportunityId,
      error: error?.message,
    });
    throw error;
  }
}

/**
 * Atomically select a provider for the opportunity (D-013).
 * @param {string} opportunityId
 * @param {{ invitationId?: string, partnerId?: string }} selection
 * @returns {Promise<object>}
 */
export async function selectProvider(opportunityId, selection = {}) {
  if (!opportunityId) {
    throw new Error("opportunityId is required");
  }
  const body = {};
  if (selection.invitationId) body.invitationId = selection.invitationId;
  if (selection.partnerId) body.partnerId = selection.partnerId;
  if (!body.invitationId && !body.partnerId) {
    throw new Error("invitationId or partnerId is required");
  }

  try {
    const response = await api.post(
      `/provider-opportunities/${encodeURIComponent(opportunityId)}/select`,
      body
    );
    if (response?.success === false) {
      throw new Error(response.error || "Failed to select provider");
    }
    return response;
  } catch (error) {
    logError("providerOpportunityService.selectProvider failed", {
      opportunityId,
      invitationId: selection.invitationId,
      partnerId: selection.partnerId,
      error: error?.message,
    });
    throw error;
  }
}

/**
 * List matched providers for a ProviderOpportunity (W15-E11 ops invite UI).
 * @param {string} opportunityId
 * @returns {Promise<object[]>}
 */
export async function getMatches(opportunityId) {
  if (!opportunityId) return [];
  try {
    const response = await api.get(
      `/provider-opportunities/${encodeURIComponent(opportunityId)}/matches`
    );
    if (response?.success === false) {
      throw new Error(response.error || "Failed to load matches");
    }
    const matches = response?.matches ?? response?.data?.matches ?? [];
    return Array.isArray(matches) ? matches : [];
  } catch (error) {
    logError("providerOpportunityService.getMatches failed", {
      opportunityId,
      error: error?.message,
    });
    throw error;
  }
}

/**
 * Send provider invitations for selected partners or all eligible matches.
 * @param {string} opportunityId
 * @param {{ partnerIds?: string[], inviteAllEligible?: boolean }} payload
 * @returns {Promise<object>}
 */
export async function sendInvitations(opportunityId, payload = {}) {
  if (!opportunityId) {
    throw new Error("opportunityId is required");
  }

  const body = {};
  if (payload.inviteAllEligible === true) {
    body.inviteAllEligible = true;
  } else if (Array.isArray(payload.partnerIds) && payload.partnerIds.length > 0) {
    body.partnerIds = payload.partnerIds;
  } else {
    throw new Error("partnerIds or inviteAllEligible is required");
  }

  try {
    const response = await api.post(
      `/provider-opportunities/${encodeURIComponent(opportunityId)}/invitations`,
      body
    );
    if (response?.success === false) {
      throw new Error(response.error || "Failed to send invitations");
    }
    return response;
  } catch (error) {
    logError("providerOpportunityService.sendInvitations failed", {
      opportunityId,
      partnerIds: payload.partnerIds,
      inviteAllEligible: payload.inviteAllEligible,
      error: error?.message,
    });
    throw error;
  }
}

export default {
  getOpportunityByOrderId,
  getCandidates,
  getMatches,
  selectProvider,
  sendInvitations,
};
